import { useMemo } from 'react'

import {
  useActiveBranchRecord,
  useCurrentIngestSession,
  useGetAuthzMeQuery,
  useGetIngestJobsPageQuery,
  type IngestSession
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Must match `SESSION_PRESENCE_PAGE_SIZE` in
 * `state/src/api/ingest/session-presence.ts` so this subscription shares the
 * jobs-page cache with `useCurrentIngestSession` instead of opening a second
 * fetch.
 */
const SESSION_PRESENCE_PAGE_SIZE = 50

export type UnstructuredIngestSession = Extract<IngestSession, { kind: 'unstructured' }>

/**
 * The ingest session that has an agent pane. Structured runs paint through
 * PlantChangesBar instead and have no conversation to show.
 */
export function useAgentIngestSession(): {
  session: UnstructuredIngestSession | undefined
  isActive: boolean
  hasFailed: boolean
  isSettled: boolean
} {
  const branch = useActiveBranchRecord()
  const { session: paintSession, outcome } = useCurrentIngestSession()
  const canReadIngest = useGetAuthzMeQuery().data?.permissions.includes('ingest/read') === true
  const querySkipped = !canReadIngest || !branch?.id || branch.protected
  const { isUninitialized, isLoading } = useGetIngestJobsPageQuery(
    {
      kind: 'all',
      page: 0,
      pageSize: SESSION_PRESENCE_PAGE_SIZE,
      branchId: branch?.id
    },
    {
      skip: querySkipped,
      refetchOnMountOrArgChange: true
    }
  )

  const session = paintSession?.kind === 'unstructured' ? paintSession : undefined
  const isSettled = querySkipped || (!isUninitialized && !isLoading)

  return useMemo(
    () => ({
      session,
      isActive: session !== undefined && outcome === 'pending',
      hasFailed: session !== undefined && outcome === 'failed',
      isSettled
    }),
    [isSettled, outcome, session]
  )
}
